// src/utils/portalApi.js
// Axios instance for the member portal. Separate from the staff `api` so
// staff and member sessions can coexist without stomping on each other's tokens.
import axios from 'axios';

const TOKEN_KEY = 'njjba_member_access_token';

export const portalApi = axios.create({
  baseURL: '/api',
  withCredentials: true, // for the member refresh cookie
});

// ─── Request interceptor: attach Bearer token ────────────────────────────────
portalApi.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor: refresh on 401, retry once ────────────────────────
let refreshing = null;

portalApi.interceptors.response.use(
  res => res,
  async (err) => {
    const original = err.config;
    const status   = err.response?.status;

    if (status !== 401 || original._retry || original.url?.includes('/auth/member/')) {
      return Promise.reject(err);
    }
    original._retry = true;

    try {
      if (!refreshing) {
        refreshing = axios.post('/api/auth/member/refresh', null, { withCredentials: true })
          .finally(() => { refreshing = null; });
      }
      const r = await refreshing;
      const newToken = r.data.accessToken;
      if (newToken) {
        localStorage.setItem(TOKEN_KEY, newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return portalApi(original);
      }
    } catch (_) {
      // refresh failed — clear token and redirect to login
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/portal/login')) {
        window.location.href = '/portal/login';
      }
    }
    return Promise.reject(err);
  }
);

export const portalTokenKey = TOKEN_KEY;
export default portalApi;
